import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ImpactSlide {
  id: string;
  /** Teinte de l'accent latéral : budget (accent) vs enveloppe (partenaire B). */
  tone: 'budget' | 'envelope';
  title: string;
  rows: Array<{ label: string; value: string }>;
}

interface ImpactCarouselProps {
  /** Titre du panneau (en-tête). */
  title: string;
  slides: ImpactSlide[];
  /** Intervalle de rotation auto en ms (défaut 5000). 0 = pas de rotation. */
  intervalMs?: number;
}

/**
 * Carrousel des impacts prévisionnels (budgets + enveloppes) — colonne latérale
 * des pages d'opérations en thème Méridien.
 *
 * Rotation automatique toujours dans le même sens (« next »), en pause au survol
 * et au focus clavier, désactivée si l'utilisateur préfère les mouvements réduits
 * ou s'il y a moins de deux slides. Chevrons ‹ › + points pour la navigation
 * manuelle. Ne modifie aucune donnée : c'est un affichage alternatif de
 * l'information déjà présente dans la carte prévisionnelle.
 */
export default function ImpactCarousel({ title, slides, intervalMs = 5000 }: ImpactCarouselProps) {
  const { t } = useTranslation('dashboard');
  const [index, setIndex] = useState(0);
  const pausedRef = useRef(false);
  const count = slides.length;

  // Recale l'index si la liste rétrécit (mois avec moins d'impacts).
  useEffect(() => {
    if (index > count - 1) setIndex(Math.max(0, count - 1));
  }, [count, index]);

  // Rotation automatique (même sens), en pause au survol/focus.
  useEffect(() => {
    if (count < 2 || intervalMs <= 0) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = window.setInterval(() => {
      if (!pausedRef.current) setIndex((i) => (i + 1) % count);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [count, intervalMs]);

  if (count === 0) return null;

  const safeIndex = Math.min(index, count - 1);
  const slide = slides[safeIndex];
  const showNav = count > 1;
  const go = (dir: number) => setIndex((i) => (i + dir + count) % count);

  return (
    <section
      className="panel opm-car"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onFocusCapture={() => { pausedRef.current = true; }}
      onBlurCapture={() => { pausedRef.current = false; }}
      aria-roledescription="carousel"
      aria-label={title}
    >
      <div className="panel-h">
        <h3>{title}</h3>
        {showNav && (
          <div className="opm-car-nav">
            <button
              type="button"
              className="opm-car-arrow"
              onClick={() => go(-1)}
              aria-label={t('impactCarousel.previous')}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="opm-car-count num">{safeIndex + 1}/{count}</span>
            <button
              type="button"
              className="opm-car-arrow"
              onClick={() => go(1)}
              aria-label={t('impactCarousel.next')}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="opm-car-body" data-tone={slide.tone}>
        <div className="opm-car-slide" aria-live="polite">
          <div className="opm-car-title">{slide.title}</div>
          {slide.rows.map((r, i) => (
            <div key={i} className="opm-car-row">
              <span className="opm-car-lbl">{r.label}</span>
              <span className="opm-car-val num">{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      {showNav && (
        <div className="opm-car-dots" role="tablist" aria-label={title}>
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === safeIndex}
              aria-label={`${i + 1}`}
              className={`opm-car-dot${i === safeIndex ? ' on' : ''}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
